import React, { useState } from 'react';
import { Mail, Lock, User, Building, Calendar, FileText } from 'lucide-react';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';
import { UNIVERSITIES } from '../../lib/constants';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';

interface LoginFormProps {
  onLogin: (userType: 'candidate' | 'evaluator' | 'admin') => void;
}

export default function LoginForm({ onLogin }: LoginFormProps) {
  const [mode, setMode] = useState<'login' | 'register-candidate' | 'register-evaluator'>('login');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Login form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  
  // Candidate registration state
  const [candidateData, setCandidateData] = useState({
    name: '',
    email: '',
    password: '',
    age: '',
    university: '', // 'tsukuba' | 'chiba' | 'gunma'
    affiliation: '',
    department: '',
    research_topic: '',
    research_overview: '',
    has_collaborators: false,
    is_grad_student: false,
    grad_program: '', // 'doctor' | 'master'
    grad_year: ''
  });

  // Evaluator (participant) registration state
  const [evaluatorData, setEvaluatorData] = useState({
    name: '',
    email: '',
    password: '',
    university: '',
    affiliation: '',
    department: '',
    is_grad_student: false,
    grad_program: '',
    grad_year: ''
  });

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) throw error;

      // Check user type
      const { data: adminData } = await supabase
        .from('admin_users')
        .select('*')
        .eq('user_id', data.user.id)
        .maybeSingle();

      if (adminData) {
        onLogin('admin');
        return;
      }

      const { data: candidateData } = await supabase
        .from('candidates')
        .select('*')
        .eq('email', email)
        .maybeSingle();

      if (candidateData) {
        onLogin('candidate');
        return;
      }

      const { data: evaluatorData } = await supabase
        .from('evaluators')
        .select('*')
        .eq('email', email)
        .maybeSingle();

      if (evaluatorData) {
        onLogin('evaluator');
        return;
      }

      throw new Error('アカウント情報に不整合があります。管理者にお問い合わせください。');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCandidateRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validate password length
    if (candidateData.password.length < 6) {
      setError('パスワードは6文字以上で入力してください');
      setLoading(false);
      return;
    }

    if (!candidateData.university) {
      setError('所属大学を選択してください');
      setLoading(false);
      return;
    }

    try {
      // Create auth user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: candidateData.email,
        password: candidateData.password
      });

      if (authError) throw authError;

      // Create candidate record
      const { error: candidateError } = await supabase
        .from('candidates')
        .insert({
          name: candidateData.name,
          email: candidateData.email,
          age: parseInt(candidateData.age),
          university: candidateData.university,
          affiliation: candidateData.affiliation,
          department: candidateData.department || null,
          research_topic: candidateData.research_topic,
          research_overview: candidateData.research_overview,
          has_collaborators: candidateData.has_collaborators,
          is_grad_student: candidateData.is_grad_student,
          grad_program: candidateData.grad_program || null,
          grad_year: candidateData.grad_year ? parseInt(candidateData.grad_year) : null
        });

      if (candidateError) throw candidateError;

      // Also create evaluator profile so presenter can comment/evaluate others
      const { error: evaluatorCreateError } = await supabase
        .from('evaluators')
        .insert({
          name: candidateData.name,
          email: candidateData.email,
          university: candidateData.university,
          affiliation: candidateData.affiliation,
          department: candidateData.department || null,
          is_grad_student: candidateData.is_grad_student,
          grad_program: candidateData.grad_program || null,
          grad_year: candidateData.grad_year ? parseInt(candidateData.grad_year) : null
        });
      if (evaluatorCreateError) {
        // Non-fatal: if already exists, proceed
        console.warn('Evaluator creation issue:', evaluatorCreateError.message);
      }

      onLogin('candidate');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEvaluatorRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validate password length
    if (evaluatorData.password.length < 6) {
      setError('パスワードは6文字以上で入力してください');
      setLoading(false);
      return;
    }

    if (!evaluatorData.university) {
      setError('所属大学を選択してください');
      setLoading(false);
      return;
    }

    try {
      // Create auth user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: evaluatorData.email,
        password: evaluatorData.password
      });

      if (authError) throw authError;

      // Create evaluator record
      const { error: evaluatorError } = await supabase
        .from('evaluators')
        .insert({
          name: evaluatorData.name,
          email: evaluatorData.email,
          university: evaluatorData.university,
          affiliation: evaluatorData.affiliation,
          department: evaluatorData.department || null,
          is_grad_student: evaluatorData.is_grad_student,
          grad_program: evaluatorData.grad_program || null,
          grad_year: evaluatorData.grad_year ? parseInt(evaluatorData.grad_year) : null
        });

      if (evaluatorError) throw evaluatorError;

      onLogin('evaluator');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted flex flex-col items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-6">
        {!isSupabaseConfigured && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            環境変数が未設定です。Vercel 環境に <code>VITE_SUPABASE_URL</code> と <code>VITE_SUPABASE_ANON_KEY</code> を設定してください。
          </div>
        )}
        <div className="text-center space-y-4">
          <div className="space-y-2">
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">
              ToRA-SEED
            </h1>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
              チャレンジファンド
            </h2>
          </div>

          <div className="mt-8 flex items-center justify-center space-x-2">
            <FileText className="h-6 w-6 text-primary" />
            <p className="text-lg text-gray-600 font-medium">
              マッチング支援システム
            </p>
          </div>
        </div>

        {error && (
          <Card className="border-destructive/50 bg-destructive/10">
            <CardContent className="pt-6">
              <p className="text-sm text-destructive">{error}</p>
            </CardContent>
          </Card>
        )}

        {mode === 'login' && (
          <Card>
            <CardHeader>
              <CardTitle>ログイン</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">メールアドレス</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="email"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password">パスワード</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="password"
                        type="password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pl-10"
                        placeholder="パスワード"
                      />
                    </div>
                  </div>
                </div>

                <Button type="submit" disabled={loading} className="w-full">
                  {loading ? 'ログイン中...' : 'ログイン'}
                </Button>

                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setMode('register-candidate')}
                    className="flex-1"
                  >
                  発表者登録
                </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setMode('register-evaluator')}
                    className="flex-1"
                  >
                  参加者登録
                </Button>
              </div>
              </form>
            </CardContent>
          </Card>
        )}

        {mode === 'register-candidate' && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>発表者登録</CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  type="button"
                  onClick={() => setMode('login')}
                >
                  ログインに戻る
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCandidateRegister} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="candidate-name">名前 *</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="candidate-name"
                        type="text"
                        required
                        value={candidateData.name}
                        onChange={(e) => setCandidateData({...candidateData, name: e.target.value})}
                        className="pl-10"
                        placeholder="山田太郎"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="candidate-age">年齢 *</Label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="candidate-age"
                        type="number"
                        required
                        min="1"
                        max="100"
                        value={candidateData.age}
                        onChange={(e) => setCandidateData({...candidateData, age: e.target.value})}
                        className="pl-10"
                        placeholder="25"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="candidate-email">メールアドレス *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="candidate-email"
                      type="email"
                      required
                      value={candidateData.email}
                      onChange={(e) => setCandidateData({...candidateData, email: e.target.value})}
                      className="pl-10"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="candidate-password">パスワード *</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="candidate-password"
                      type="password"
                      required
                      value={candidateData.password}
                      onChange={(e) => setCandidateData({...candidateData, password: e.target.value})}
                      className="pl-10"
                      placeholder="パスワード"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="candidate-university">所属 *</Label>
                  <div className="relative">
                    <Building className="absolute left-3 top-3 h-4 w-4 text-muted-foreground pointer-events-none" />
                    <select
                      id="candidate-university"
                      required
                      value={candidateData.university}
                      onChange={(e) => {
                        const id = e.target.value;
                        const uni = UNIVERSITIES.find((u) => u.id === id);
                        setCandidateData({
                          ...candidateData,
                          university: id,
                          affiliation: uni?.name ?? '',
                        });
                      }}
                      className="w-full border rounded-md h-9 pl-10 pr-3 bg-background text-sm"
                    >
                      <option value="">大学を選択してください</option>
                      {UNIVERSITIES.map((u) => (
                        <option key={u.id} value={u.id}>{u.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="candidate-department">系・部署・診療科</Label>
                  <div className="relative">
                    <Building className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="candidate-department"
                      type="text"
                      value={candidateData.department}
                      onChange={(e) => setCandidateData({...candidateData, department: e.target.value})}
                      className="pl-10"
                      placeholder="○○学部、○○部門、○○診療科など"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="candidate-topic">研究課題名 *</Label>
                  <Input
                    id="candidate-topic"
                    type="text"
                    required
                    value={candidateData.research_topic}
                    onChange={(e) => setCandidateData({...candidateData, research_topic: e.target.value})}
                    placeholder="研究課題のタイトル"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="candidate-overview">研究概要（任意）</Label>
                  <Textarea
                    id="candidate-overview"
                    rows={4}
                    value={candidateData.research_overview}
                    onChange={(e) => setCandidateData({...candidateData, research_overview: e.target.value})}
                    placeholder="研究の概要を記述してください（任意）"
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="has_collaborators"
                    checked={candidateData.has_collaborators}
                    onCheckedChange={(checked) => setCandidateData({...candidateData, has_collaborators: !!checked})}
                  />
                  <Label htmlFor="has_collaborators" className="text-sm">
                    共同研究者がいる
                  </Label>
                </div>

                <Button type="submit" disabled={loading} className="w-full">
                  {loading ? '登録中...' : '発表者として登録'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {mode === 'register-evaluator' && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>参加者登録</CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  type="button"
                  onClick={() => setMode('login')}
                >
                  ログインに戻る
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleEvaluatorRegister} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="evaluator-name">名前 *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="evaluator-name"
                      type="text"
                      required
                      value={evaluatorData.name}
                      onChange={(e) => setEvaluatorData({...evaluatorData, name: e.target.value})}
                      className="pl-10"
                      placeholder="山田太郎"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="evaluator-university">所属 *</Label>
                  <div className="relative">
                    <Building className="absolute left-3 top-3 h-4 w-4 text-muted-foreground pointer-events-none" />
                    <select
                      id="evaluator-university"
                      required
                      value={evaluatorData.university}
                      onChange={(e) => {
                        const id = e.target.value;
                        const uni = UNIVERSITIES.find((u) => u.id === id);
                        setEvaluatorData({
                          ...evaluatorData,
                          university: id,
                          affiliation: uni?.name ?? '',
                        });
                      }}
                      className="w-full border rounded-md h-9 pl-10 pr-3 bg-background text-sm"
                    >
                      <option value="">大学を選択してください</option>
                      {UNIVERSITIES.map((u) => (
                        <option key={u.id} value={u.id}>{u.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="evaluator-department">系・部署・診療科（任意）</Label>
                  <Input
                    id="evaluator-department"
                    type="text"
                    value={evaluatorData.department}
                    onChange={(e) => setEvaluatorData({...evaluatorData, department: e.target.value})}
                    placeholder="○○学部、○○部門、○○診療科など"
                  />
                </div>

                {/* 大学院生情報（任意・条件付き） */}
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="evaluator-is-grad"
                      checked={evaluatorData.is_grad_student}
                      onCheckedChange={(checked) => setEvaluatorData({...evaluatorData, is_grad_student: !!checked})}
                    />
                    <Label htmlFor="evaluator-is-grad" className="text-sm">大学院生（修士/博士）</Label>
                  </div>
                  {evaluatorData.is_grad_student && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="evaluator-grad-program">課程（博士・修士）</Label>
                        <select
                          id="evaluator-grad-program"
                          className="w-full border rounded-md h-9 px-3 bg-background"
                          value={evaluatorData.grad_program}
                          onChange={(e) => setEvaluatorData({...evaluatorData, grad_program: e.target.value})}
                        >
                          <option value="">選択してください</option>
                          <option value="doctor">博士</option>
                          <option value="master">修士</option>
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="evaluator-grad-year">学年</Label>
                        <Input
                          id="evaluator-grad-year"
                          type="number"
                          min="1"
                          max="6"
                          value={evaluatorData.grad_year}
                          onChange={(e) => setEvaluatorData({...evaluatorData, grad_year: e.target.value})}
                          placeholder="1"
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="evaluator-email">メールアドレス *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="evaluator-email"
                      type="email"
                      required
                      value={evaluatorData.email}
                      onChange={(e) => setEvaluatorData({...evaluatorData, email: e.target.value})}
                      className="pl-10"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="evaluator-password">パスワード *</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="evaluator-password"
                      type="password"
                      required
                      value={evaluatorData.password}
                      onChange={(e) => setEvaluatorData({...evaluatorData, password: e.target.value})}
                      className="pl-10"
                      placeholder="パスワード"
                    />
                  </div>
                </div>

                <Button type="submit" disabled={loading} className="w-full">
                  {loading ? '登録中...' : '参加者として登録'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
      <p className="mt-8 text-[11px] text-muted-foreground/70">Produced by 株式会社amiac one</p>
    </div>
  );
}
